import { Moment } from "moment";
import { useEffect, useState } from "react";
import {
  AddressResponse,
  DietitianAppointmentAnonymousRequestDietaryGoalsEnum,
  DietitianAppointmentAnonymousRequestGenderEnum,
  UserResponseGenderEnum,
} from "../../openapi/client";
import { checkEmpty, checkEmptyObj, checkIf, checkUndefined, gulfCountries } from "../../utils";
import { useTranslation } from "react-i18next";

// Helper function to validate phone number against Gulf countries
const validateGulfPhoneNumber = (phoneNumber: string, t: any) => {
  if (!phoneNumber) return { isValid: false, helperText: t("phoneNumberRequired") };

  // Find the country that matches the phone number prefix
  const matchingCountry = gulfCountries.find((country) => phoneNumber.startsWith(country.dialCode));

  if (!matchingCountry) {
    return {
      isValid: false,
      helperText: t("phoneNumberInvalidCountry"),
    };
  }

  // Extract the local number (without country code)
  const localNumber = phoneNumber.slice(matchingCountry.dialCode.length);

  // Test against the country's regex
  const isValid = matchingCountry.regex.test(localNumber);

  // Map country codes to translation keys
  const phoneFormatKeyMap: { [key: string]: string } = {
    QA: "phoneNumberFormatQA",
    SA: "phoneNumberFormatSA",
    AE: "phoneNumberFormatAE",
    KW: "phoneNumberFormatKW",
    BH: "phoneNumberFormatBH",
    OM: "phoneNumberFormatOM",
  };

  return {
    isValid,
    helperText: isValid ? "" : t(phoneFormatKeyMap[matchingCountry.code] || "phoneNumberFormat"),
  };
};

export interface IValidateEntity {
  error?: boolean;
  helperText?: string;
  dirty?: boolean;
}

export interface IValidate {
  show?: boolean;
  error?: boolean;
  dirty?: boolean;
  companyName?: IValidateEntity;
  legalName?: IValidateEntity;
  customerSize?: IValidateEntity;
  fullName?: IValidateEntity;
  contactName?: IValidateEntity;
  title?: IValidateEntity;
  email?: IValidateEntity;
  crNumber?: IValidateEntity;
  message?: IValidateEntity;
  reason?: IValidateEntity;
  dietaryGoals?: IValidateEntity;
  additionalNotes?: IValidateEntity;
  dateTime?: IValidateEntity;
  dietitianName?: IValidateEntity;
  partnerId?: IValidateEntity;
  dateOfBirth?: IValidateEntity;
  gender?: IValidateEntity;
  contactNumber?: IValidateEntity;
  mail?: IValidateEntity;
  address?: IValidateEntity;
  phoneNumber?: IValidateEntity;
  contactMail?: IValidateEntity;
  url?: IValidateEntity;
}

export interface IProps {
  fullName?: string;
  title?: string;
  email?: string;
  message?: string;
  reason?: string;
  companyName?: string;
  dietaryGoals?: Array<DietitianAppointmentAnonymousRequestDietaryGoalsEnum>;
  additionalNotes?: string;
  dateTime?: Moment | null;
  dietitianName?: string;
  partnerId?: string;
  dateOfBirth?: Moment | null;
  gender?: DietitianAppointmentAnonymousRequestGenderEnum | null;
  contactNumber?: string;
  mail?: string;
  address?: string;
  phoneNumber?: string;
  contactMail?: string;
  url?: string;
  contactName?: string;
}

export default function useContactValidation(info: IProps) {
  const { t, i18n } = useTranslation("validation");
  const [validation, setValidation] = useState<IValidate>({
    show: false,
    error: false,
    dirty: false,
  });

  useEffect(() => {
    if (!checkIf(info) || checkEmptyObj(info)) {
      return;
    }

    setValidation((state) => {
      let newState = { ...state } as IValidate;
      if (Object.keys(validation).length === 3) {
        let fields: any = {};
        Object.keys(info).forEach((item: string) => {
          fields[item] = { error: false, helperText: "" };
        });
        newState = { ...newState, ...fields };
      }

      newState.error = false;

      if (!checkUndefined(info.companyName)) {
        if (checkEmpty(info?.companyName) || !checkIf(info?.companyName)) {
          newState.companyName!.error = true;
          newState.companyName!.helperText = t("companyNameRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.companyName!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          newState.companyName!.error = status;
          newState.companyName!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.address)) {
        if (checkEmpty(info?.address) || !checkIf(info?.address)) {
          newState.address!.error = true;
          newState.address!.helperText = t("addressRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.address!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          newState.address!.error = status;
          newState.address!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.reason)) {
        if (checkEmpty(info?.reason) || !checkIf(info?.reason)) {
          newState.reason!.error = true;
          newState.reason!.helperText = t("reasonRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.reason!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          newState.reason!.error = status;
          newState.reason!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.fullName)) {
        if (checkEmpty(info?.fullName) || !checkIf(info?.fullName)) {
          if (newState.fullName) {
            newState.fullName.error = true;
            newState.fullName.helperText = t("fullNameRequired");
            newState.fullName.dirty = false;
          } else {
            newState.fullName = {
              error: true,
              helperText: t("fullNameRequired"),
              dirty: false,
            };
          }
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.fullName!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          if (newState.fullName) {
            newState.fullName.error = status;
            newState.fullName.helperText = message;
          } else {
            newState.fullName = {
              error: status,
              helperText: message,
              dirty: true,
            };
          }

          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.contactName)) {
        if (checkEmpty(info?.contactName) || !checkIf(info?.contactName)) {
          if (newState.contactName) {
            newState.contactName.error = true;
            newState.contactName.helperText = t("contactNameRequired");
            newState.contactName.dirty = false;
          } else {
            newState.contactName = {
              error: true,
              helperText: t("contactNameRequired"),
              dirty: false,
            };
          }
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.contactName!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          if (newState.contactName) {
            newState.contactName.error = status;
            newState.contactName.helperText = message;
          } else {
            newState.contactName = {
              error: status,
              helperText: message,
              dirty: true,
            };
          }

          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.title)) {
        if (checkEmpty(info?.title) || !checkIf(info?.title)) {
          newState.title!.error = true;
          newState.title!.helperText = t("titleRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.title!.length > 250) {
            message = t("maxChars250");
            status = true;
          }
          newState.title!.error = status;
          newState.title!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.email)) {
        if (checkEmpty(info?.email) || !checkIf(info?.email)) {
          newState.email!.error = true;
          newState.email!.helperText = t("emailRequired");
          newState.error = true;
        } else {
          const regex = new RegExp(
            "([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|\"([]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|[[\t -Z^-~]*])"
          );
          const test = regex.test(String(info.email));
          const status = !test;
          newState.email!.error = status;
          newState.email!.helperText = status ? t("emailRequired") : "";
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.mail)) {
        if (checkEmpty(info?.mail) || !checkIf(info?.mail)) {
          newState.mail!.error = true;
          newState.mail!.helperText = t("emailRequired");
          newState.error = true;
        } else {
          const regex = new RegExp(
            "([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|\"([]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|[[\t -Z^-~]*])"
          );
          const test = regex.test(String(info.mail));
          const status = !test;
          newState.mail!.error = status;
          newState.mail!.helperText = status ? t("emailRequired") : "";
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.url)) {
        if (checkEmpty(info?.url) || !checkIf(info?.url)) {
          newState.url!.error = true;
          newState.url!.helperText = t("urlRequired");
          newState.error = true;
        } else {
          const regex = new RegExp(
            "(https?://(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|https?://(?:www.|(?!www))[a-zA-Z0-9]+.[^s]{2,}|www.[a-zA-Z0-9]+.[^s]{2,})"
          );
          const test = regex.test(String(info.url));
          const status = !test;
          newState.url!.error = status;
          newState.url!.helperText = status ? t("urlRequired") : "";
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.contactMail)) {
        if (checkEmpty(info?.contactMail) || !checkIf(info?.contactMail)) {
          newState.contactMail!.error = true;
          newState.contactMail!.helperText = t("emailRequired");
          newState.error = true;
        } else {
          const regex = new RegExp(
            "([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|\"([]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|[[\t -Z^-~]*])"
          );
          const test = regex.test(String(info.contactMail));
          const status = !test;
          newState.contactMail!.error = status;
          newState.contactMail!.helperText = status ? t("emailRequired") : "";
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.message)) {
        if (checkEmpty(info?.message) || !checkIf(info?.message)) {
          newState.message!.error = true;
          newState.message!.helperText = t("messageRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.message!.length > 500) {
            message = t("maxChars500");
            status = true;
          }
          newState.message!.error = status;
          newState.message!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (info.gender !== undefined) {
        if (checkEmpty(info?.gender) || !checkIf(info?.gender)) {
          if (newState.gender) {
            newState.gender.error = true;
            newState.gender.helperText = t("genderRequired");
          } else {
            newState.gender = {
              error: true,
              helperText: t("genderRequired"),
              dirty: false,
            };
          }
          newState.error = true;
        } else {
          newState.gender!.error = false;
        }
      }

      if (info.dateOfBirth !== undefined) {
        if (checkEmpty(info?.dateOfBirth) || !checkIf(info?.dateOfBirth)) {
          if (newState.dateOfBirth) {
            newState.dateOfBirth!.error = true;
            newState.dateOfBirth!.helperText = t("dateOfBirthRequired");
          } else {
            newState.dateOfBirth = {
              error: true,
              helperText: t("dateOfBirthRequired"),
              dirty: false,
            };
          }

          newState.error = true;
        } else {
          newState.dateOfBirth!.error = false;
        }
      }

      if (!checkUndefined(info.dietaryGoals)) {
        if (checkEmpty(info?.dietaryGoals) || !checkIf(info?.dietaryGoals)) {
          newState.dietaryGoals!.error = true;
          newState.dietaryGoals!.helperText = t("dietaryGoalsRequired");
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          if (info.dietaryGoals!.length === 0) {
            message = t("dietaryGoalsSelect");
            status = true;
          }
          newState.dietaryGoals!.error = status;
          newState.dietaryGoals!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(info.dateTime)) {
        if (checkEmpty(info?.dateTime) || !checkIf(info?.dateTime)) {
          newState.dateTime!.error = true;
          newState.dateTime!.helperText = t("dateTimeRequired");
          newState.error = true;
        } else {
          newState.dateTime!.error = false;
        }
      }
      if (!checkUndefined(info.contactNumber)) {
        if (checkEmpty(info?.contactNumber) || !checkIf(info?.contactNumber)) {
          const helperText = t("phoneNumberRequired");
          if (newState.contactNumber) {
            newState.contactNumber.error = true;
            newState.contactNumber.helperText = helperText;
          } else {
            newState.contactNumber = {
              error: true,
              helperText,
              dirty: false,
            };
          }
          newState.error = true;
        } else {
          const validation = validateGulfPhoneNumber(info.contactNumber!, t);
          const status = !validation.isValid;
          newState.contactNumber!.error = status;
          if (status) {
            newState.contactNumber!.helperText = validation.helperText;
            newState.error = true;
          }
        }
      }
      if (!checkUndefined(info.phoneNumber)) {
        if (checkEmpty(info?.phoneNumber) || !checkIf(info?.phoneNumber)) {
          const helperText = t("phoneNumberRequired");
          if (newState.phoneNumber) {
            newState.phoneNumber.error = true;
            newState.phoneNumber.helperText = helperText;
          } else {
            newState.phoneNumber = {
              error: true,
              helperText,
              dirty: false,
            };
          }
          newState.error = true;
        } else {
          const validation = validateGulfPhoneNumber(info.phoneNumber!, t);
          const status = !validation.isValid;
          newState.phoneNumber!.error = status;
          if (status) {
            newState.phoneNumber!.helperText = validation.helperText;
            newState.error = true;
          }
        }
      }

      return newState;
    });
  }, [info, t]);
  return { validation, setValidation };
}
